<template>
  <div>
    <div class="role-toolbar">
      <el-button type="primary" size="small" icon="el-icon-plus" @click="openDialog()">新增角色</el-button>
      <span class="role-count">共 {{ validRoles.length }} 个角色</span>
    </div>

    <div class="role-list">
      <div class="role-item" v-for="role in validRoles" :key="role.id">
        <div class="role-avatar-wrap">
          <img v-if="role.avatar_url" :src="role.avatar_url" alt="avatar" class="role-avatar" loading="lazy" />
          <div v-else class="role-avatar role-avatar--empty"><i class="el-icon-user"></i></div>
        </div>
        <div class="role-info">
          <div class="role-name">{{ role.name || '-' }}</div>
          <div class="role-tags">
            <el-tag size="mini" :type="role.role_type === 'USER' ? 'warning' : role.role_type === 'NARRATOR' ? 'info' : ''">{{ getRoleTypeLabel(role.role_type) }}</el-tag>
            <el-tag v-if="role.child_role_ids && role.child_role_ids.length > 0" size="mini" type="success">共享{{ role.child_role_ids.length }}个角色</el-tag>
            <el-tag v-if="role.parent_role_id" size="mini" type="info">共享自{{ getParentRoleName(role.parent_role_id) }}</el-tag>
            <span v-if="role.voice_type" class="role-voice">{{ getVoiceLabel(role.voice_type) }}</span>
          </div>
        </div>
        <span class="role-id">#{{ role.id }}</span>
        <div class="role-actions">
          <el-button size="mini" icon="el-icon-edit" @click="openDialog(role)">编辑</el-button>
          <el-button size="mini" type="danger" icon="el-icon-delete" @click="deleteRole(role.id)">删除</el-button>
        </div>
      </div>
      <div v-if="validRoles.length === 0" class="role-empty">暂无角色</div>
    </div>

    <el-dialog :title="form.id ? '编辑角色' : '新增角色'" :visible.sync="dialogVisible" width="700px" append-to-body>
      <el-form :model="form" label-width="100px">
        <el-form-item label="名称"><el-input v-model="form.name" /></el-form-item>

        <!-- 属性共享（别名）：关联子角色 -->
        <el-form-item label="属性共享">
          <template v-if="form.parent_role_id">
            <el-tag type="warning">该角色已被「{{ getParentRoleName(form.parent_role_id) }}」关联，无法再关联其他角色</el-tag>
          </template>
          <template v-else>
            <el-select
              v-model="form.child_role_ids"
              multiple
              placeholder="选择要共享属性的角色"
              style="width: 100%;"
              filterable
            >
              <el-option
                v-for="role in availableChildRoles"
                :key="role.id"
                :label="role.name"
                :value="role.id"
              />
            </el-select>
            <div style="font-size: 12px; color: #909399; margin-top: 4px;">
              被关联的角色将共享该角色的属性配置和数值
            </div>
          </template>
        </el-form-item>

        <!-- 立绘 -->
        <el-form-item label="立绘">
          <div>
            <img
              v-if="form.avatar_url"
              :src="form.avatar_url"
              style="width: 100px; height: 200px; object-fit: cover; display: block; margin-bottom: 8px; border-radius: 4px; border: 1px solid #e5e5e5;"
            />
            <el-button size="small" type="primary" @click="showPortraitUploader = true">选择立绘</el-button>
          </div>
        </el-form-item>

        <!-- 音色 -->
        <el-form-item label="音色">
          <div style="display: flex; gap: 8px; align-items: center;">
            <el-select v-model="form.voice_type" placeholder="请选择音色" style="flex: 1;" filterable>
              <el-option
                v-for="vt in voiceTypes"
                :key="vt.value"
                :label="vt.label"
                :value="vt.value"
              />
            </el-select>
            <el-button
              size="small"
              type="primary"
              :loading="playingVoice"
              :disabled="!form.voice_type || !form.name"
              @click="playVoiceTest"
              icon="el-icon-video-play"
            >
              试听
            </el-button>
          </div>
        </el-form-item>

        <!-- 角色类型 -->
        <el-form-item label="角色类型">
          <el-select v-model="form.role_type" placeholder="请选择角色类型">
            <el-option label="用户" value="USER"></el-option>
            <el-option label="NPC" value="NPC"></el-option>
            <el-option label="旁白" value="NARRATOR"></el-option>
            <el-option label="解析器" value="RESOLVER"></el-option>
          </el-select>
        </el-form-item>

        <!-- 展示在衣橱 -->
        <el-form-item label="展示在衣橱">
          <el-switch
            v-model="form.show_cos_bool"
            active-text="是"
            inactive-text="否">
          </el-switch>
        </el-form-item>

        <!-- 展示在角色介绍（客户端 app 角色介绍页） -->
        <el-form-item label="展示在角色介绍">
          <el-switch
            v-model="form.show_intr"
            active-text="是"
            inactive-text="否">
          </el-switch>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </span>
    </el-dialog>

    <ResourceUploader
      :show.sync="showPortraitUploader"
      :game-id="game.id"
      file-type="portrait"
      @select="handlePortraitSelect"
    />

    <NextNodeSelectDialog
      :show.sync="showNodeSelect"
      :all-nodes="allNodes"
      :chapters="chapters"
      @select="handleNodeSelected"
    />
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import {getVoiceType, Role, testVoice, RoleAlias} from '@/api/role'
import { getRoles, createRole, updateRole, deleteRole } from '@/api/role'
import {Game} from "@/api/game"
import ResourceUploader from '@/components/node-editor/ResourceUploader.vue'
import NextNodeSelectDialog from '@/components/node-editor/NextNodeSelectDialog.vue'
import { GameNode, getNodes } from '@/api/node'
import { Chapter, getChapters } from '@/api/chapter'

@Component({ components: { ResourceUploader, NextNodeSelectDialog } })
export default class RoleList extends Vue {
  @Prop({ required: true }) readonly game!: Game
  roles: Role[] = []
  dialogVisible = false

  voiceTypes: { label: string, value: string }[] = []
  playingVoice = false
  currentAudio: HTMLAudioElement | null = null

  showPortraitUploader = false
  showNodeSelect = false
  allNodes: GameNode[] = []
  chapters: Chapter[] = []
  nodeSelectTarget: { type: 'start' | 'end', aliasIndex: number } | null = null

  form: any = {
    id: undefined,
    name: '',
    aliases: [],
    aliases_list: [] as RoleAlias[],
    avatar_url: '',
    bio: '',
    personality: '',
    dialogue_style: '',
    initial_states: {},
    role_type: 'NPC',
    voice_type: '',
    need_affection: false,
    show_banner: false,
    show_cos: 0,
    show_cos_bool: false,
    show_intr: false,
    game_id: 0,
    parent_role_id: null as number | null,
    child_role_ids: [] as number[]
  }

  // 过滤掉 null 或没有 id 的角色
  get validRoles(): Role[] {
    return this.roles.filter(role => role && role.id)
  }

  // 可选为子角色的角色列表：排除自己、已有 parent 的角色、已有 children 的角色
  get availableChildRoles(): Role[] {
    return this.validRoles.filter(r => {
      if (r.id === this.form.id) return false
      // 已经是别人的子角色（且不是当前角色的子角色）
      if (r.parent_role_id && r.parent_role_id !== this.form.id) return false
      // 已经是父角色（有自己的子角色）
      if (r.child_role_ids && r.child_role_ids.length > 0) return false
      return true
    })
  }

  getParentRoleName(parentId: number): string {
    const role = this.validRoles.find(r => r.id === parentId)
    return role ? role.name : `角色#${parentId}`
  }

  @Watch("game", {immediate: true})
  onGameChange(newGame: Game){
    if (newGame && newGame.id) {
      this.loadData()
    }
  }

  mounted() {
    this.loadVoiceTypes()
  }

  loadData() {
    getRoles(this.game.id).then((res) => {
      this.roles = res
      console.log(`roles length: ${this.roles.length}`)
    }).catch(() => {
      this.$message.error('加载角色失败')
    })
  }

  openDialog(role?: Role) {
    if (role) {
      this.form = {
        ...role,
        show_cos_bool: role.show_cos === 1,
        show_intr: role.show_intr ?? false,
        aliases_list: (role.aliases || []).map((a: any) =>
          typeof a === 'string' ? { name: a, start_node_id: null, end_node_id: null } : { ...a }
        ),
        parent_role_id: role.parent_role_id || null,
        child_role_ids: role.child_role_ids || []
      }
    } else {
      this.form = {
        id: undefined, name: '', aliases: [], aliases_list: [],
        avatar_url: '', bio: '', personality: '', dialogue_style: '',
        initial_states: {}, role_type: 'NPC', voice_type: '',
        need_affection: false, show_banner: false, show_cos: 0,
        show_cos_bool: false, show_intr: false, game_id: this.game.id,
        parent_role_id: null, child_role_ids: []
      }
    }
    this.dialogVisible = true
  }

  submitForm() {
    // 防重复提交
    if ((this as any)._submitting) return;
    (this as any)._submitting = true;
    const payload: any = { ...this.form }
    payload.aliases = this.form.aliases_list.filter((a: RoleAlias) => a.name.trim())
    payload.show_cos = this.form.show_cos_bool ? 1 : 0
    payload.game_id = this.game.id
    payload.child_role_ids = this.form.child_role_ids || []
    delete payload.aliases_list
    delete payload.show_cos_bool
    delete payload.parent_role_id  // 由后端维护，不从前端传

    const action = this.form.id
      ? updateRole(this.form.id, payload)
      : createRole(payload)

    action.then(() => {
      this.$message.success('保存成功')
      this.dialogVisible = false
      this.loadData();
      (this as any)._submitting = false
    }).catch(() => {
      this.$message.error('保存失败');
      (this as any)._submitting = false
    })
    .finally(() => {
      (this as any)._submitting = false
    })
  }

  deleteRole(id?: number) {
    if (!id) {
      this.$message.error('角色ID无效，无法删除')
      return
    }
    this.$confirm('确定删除该角色？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      deleteRole(id).then(() => {
        this.$message.success('删除成功')
        this.loadData()
      })
    })
  }

  loadVoiceTypes() {
    getVoiceType().then((res)=>{
      console.log(`voice_type: `, res)
      this.voiceTypes = res.data;
    })
  }

  async loadNodesAndChapters() {
    try {
      const [nodesRes, chaptersRes] = await Promise.all([
        getNodes(this.game.id),
        getChapters(this.game.id)
      ])
      this.allNodes = nodesRes.data
      this.chapters = chaptersRes.data
    } catch { /* ignore */ }
  }

  openNodeSelect(type: 'start' | 'end', aliasIndex: number) {
    this.nodeSelectTarget = { type, aliasIndex }
    if (this.allNodes.length === 0) this.loadNodesAndChapters()
    this.showNodeSelect = true
  }

  handleNodeSelected(nodeId: number | null) {
    if (!this.nodeSelectTarget) return
    const alias = this.form.aliases_list[this.nodeSelectTarget.aliasIndex]
    if (this.nodeSelectTarget.type === 'start') {
      alias.start_node_id = nodeId
    } else {
      alias.end_node_id = nodeId
    }
    this.showNodeSelect = false
  }

  handlePortraitSelect(resource: any) {
    this.form.avatar_url = resource.oss_url
  }

  async playVoiceTest() {
    if (!this.form.voice_type || !this.form.name) {
      this.$message.warning('请先选择音色并填写角色名称')
      return
    }

    // 停止当前正在播放的音频
    if (this.currentAudio) {
      this.currentAudio.pause()
      this.currentAudio = null
    }

    this.playingVoice = true

    try {
      const response = await testVoice(this.form.voice_type, this.form.name)

      if (response.type === 'voice' && response.voice) {
        // Base64转为blob URL
        const byteCharacters = atob(response.voice)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: `audio/${response.format || 'mp3'}` })
        const audioUrl = URL.createObjectURL(blob)

        // 创建并播放音频
        this.currentAudio = new Audio(audioUrl)
        this.currentAudio.addEventListener('ended', () => {
          this.playingVoice = false
          URL.revokeObjectURL(audioUrl)
          this.currentAudio = null
        })
        this.currentAudio.addEventListener('error', () => {
          this.playingVoice = false
          this.$message.error('音频播放失败')
          URL.revokeObjectURL(audioUrl)
          this.currentAudio = null
        })

        await this.currentAudio.play()
        this.$message.success(`正在播放${response.role}的音色`)
      } else {
        this.$message.error('获取音频数据失败')
        this.playingVoice = false
      }
    } catch (error) {
      console.error('播放音色失败:', error)
      this.$message.error('播放音色失败')
      this.playingVoice = false
    }
  }

  getRoleTypeLabel(roleType: string): string {
    const roleTypeMap: { [key: string]: string } = {
      'USER': '用户',
      'NPC': 'NPC',
      'NARRATOR': '旁白',
      'RESOLVER': '解析器'
    }
    return roleTypeMap[roleType] || roleType || '-'
  }

  getVoiceLabel(voiceType: string): string {
    if (!voiceType) return '-'
    const voice = this.voiceTypes.find(v => v.value === voiceType)
    return voice ? voice.label : voiceType
  }
}
</script>

<style scoped>
.role-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.role-count {
  font-size: 13px;
  color: #909399;
}

.role-list {
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 8px;
}

.role-empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 14px;
}

.role-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid #f2f3f5;
  transition: background 0.15s;
}

.role-item:last-child {
  border-bottom: none;
}

.role-item:hover {
  background: #f9fafb;
}

.role-avatar-wrap {
  flex-shrink: 0;
}

.role-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ebeef5;
  display: block;
}

.role-avatar--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  color: #c0c4cc;
  font-size: 22px;
}

.role-info {
  flex: 1;
  min-width: 0;
}

.role-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.role-tags {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.role-voice {
  font-size: 12px;
  color: #909399;
}

.role-id {
  font-size: 12px;
  color: #c0c4cc;
  flex-shrink: 0;
}

.role-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

/* 别名行 */
.alias-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
</style>
